import { ref, computed } from 'vue'
import de from './de.json'
import en from './en.json'

// ----------------------------------------------------------------------
// Tiny i18n for the login library — clone of the chat-ui useI18n()
// pattern (out/chat-ui-src/src/i18n.js). Two bundled languages, one
// reactive ref for the active locale, deep-merge override pathway
// so the orchestrator's `messages` prop can add or override strings
// at mount time without touching the bundled JSON.
//
// `t(key, params)` performs simple `{placeholder}` substitution to
// support strings like errorLoginLocked: "Account locked until {until}".
//
// Vue-i18n is intentionally NOT used: we want zero peer-dep, and
// the bundle has to stay tiny (≤ a couple of KB after gzip on top
// of Vue) for the standalone widget.

const bundled = { de, en }

function detectLanguage() {
  try {
    const raw = (navigator.language || navigator.userLanguage || '').toLowerCase()
    const short = raw.substring(0, 2)
    return bundled[short] ? short : 'en'
  } catch (e) {
    return 'en'
  }
}

const currentLocale = ref(detectLanguage())
const overrides = ref({})

function lookup(locale, key) {
  const overrideTable = overrides.value[locale]
  if (overrideTable && Object.prototype.hasOwnProperty.call(overrideTable, key)) {
    return overrideTable[key]
  }
  const bundle = bundled[locale]
  if (bundle && Object.prototype.hasOwnProperty.call(bundle, key)) {
    return bundle[key]
  }
  return undefined
}

function interpolate(template, params) {
  if (!params || typeof template !== 'string') return template
  return template.replace(/\{(\w+)\}/g, (_, name) =>
    Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : `{${name}}`
  )
}

export function setLocale(lang) {
  if (lang && bundled[lang]) {
    currentLocale.value = lang
  }
}

/** Current active locale code ('de' or 'en'). */
export function getLocale() {
  return currentLocale.value
}

/**
 * Register caller-supplied translations on top of the bundled ones.
 * Shape: { de: { key: 'value' }, en: { key: 'value' } }.
 * Called by LoginOrchestrator when a host passes the `messages`
 * prop or a `data-messages-url` attribute fetches a JSON map.
 */
export function applyMessageOverrides(messages) {
  if (!messages || typeof messages !== 'object') return
  const merged = { ...overrides.value }
  for (const [lang, table] of Object.entries(messages)) {
    if (!table || typeof table !== 'object') continue
    merged[lang] = { ...(merged[lang] || {}), ...table }
  }
  overrides.value = merged
}

export function useI18n() {
  const t = (key, params) => {
    const value = lookup(currentLocale.value, key)
      ?? lookup('en', key)
      ?? lookup('de', key)
      ?? key
    return interpolate(value, params)
  }
  const locale = computed({
    get: () => currentLocale.value,
    set: (v) => setLocale(v)
  })
  const availableLocales = Object.keys(bundled)
  return { t, locale, availableLocales, setLocale, applyMessageOverrides }
}

export default useI18n
